import { useMemo } from 'react'

import { uniqBy } from 'lodash'

import { useNFTCollectionMetadata } from '~/components/txn-display/state-change/hooks/useNFTCollectionMetadata'
import { buildStateChangeItems } from '~/components/txn-display/state-change/utils/buildStateChangeItems'
import { deriveStateChangeResult } from '~/components/txn-display/state-change/utils/deriveStateChangeResult'
import { getTokenMetadataLookupKey } from '~/components/txn-display/state-change/utils/getTokenMetadataLookupKey'
import { useSimulateCalls } from '~/components/txn-display/state-change/utils/simulation'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { EvmAddress } from '~/utils/address'

import type {
  CombinedStateChangeResult,
  DecodedCallSummary,
  MetadataTarget,
  TokenMetadataLookup,
  UseStateChangesArgs,
  UseStateChangesResult,
} from '~/components/txn-display/state-change/types'
import type { SimulationError } from '~/components/txn-display/state-change/utils/simulation'
import type { TokenMetadataWithPrice } from '~/hooks/useTokenMetadata'
import type { BackendTransactionDecodedCall } from '~/types'

const EMPTY_COMBINED_RESULT: CombinedStateChangeResult = {
  stateChanges: [],
  approvalChanges: [],
  nftStateChanges: [],
}

// Hook to derive the state change items from the simulate calls data
export const useSimulatedStateChanges = ({
  smartAccountAddress,
  chainId,
  calls,
  transactionId,
  addressesToWatch,
  bridgeStateChanges,
  stateChangesToHide,
}: UseStateChangesArgs): UseStateChangesResult => {
  const normalizedAddressesToWatch = useMemo(
    () =>
      addressesToWatch
        ?.filter((address) => EvmAddress.validate(address))
        ?.map((address) => EvmAddress.format(address)) ?? [],
    [addressesToWatch],
  )
  const {
    data: simulateCallsData,
    isError,
    isLoading,
    isFetching,
    error,
  } = useSimulateCalls({
    smartAccountAddress,
    chainId,
    calls,
    transactionId,
  })

  // Derives the state change result from the simulate calls data
  const derivedResult = useMemo(() => {
    if (
      !smartAccountAddress ||
      !chainId ||
      !calls?.length ||
      !normalizedAddressesToWatch.length ||
      !simulateCallsData
    )
      return undefined

    return deriveStateChangeResult({
      addressesToWatch: normalizedAddressesToWatch,
      chainId,
      simulateCallsData,
    })
  }, [
    smartAccountAddress,
    chainId,
    calls,
    normalizedAddressesToWatch,
    simulateCallsData,
  ])

  // Builds the token metadata from the derived state change result
  const tokens = useMemo(() => {
    const uniqueTokens = new Map<string, MetadataTarget>()

    const addToken = (
      chain: MetadataTarget['chainId'],
      address: MetadataTarget['address'],
    ) => {
      const key = getTokenMetadataLookupKey(chain, address)
      if (!uniqueTokens.has(key)) {
        uniqueTokens.set(key, { chainId: chain, address })
      }
    }

    derivedResult?.stateChanges.forEach((change) =>
      addToken(change.chainId, change.token),
    )

    derivedResult?.approvalChanges.forEach((change) =>
      addToken(change.chainId, change.token),
    )

    derivedResult?.nftStateChanges.forEach((change) =>
      addToken(change.chainId, change.token),
    )

    bridgeStateChanges?.forEach((change) =>
      addToken(change.chainId, change.token),
    )

    return Array.from(uniqueTokens.values())
  }, [derivedResult, bridgeStateChanges])

  // fetches the token metadata for the metadata targets
  const tokenMetadataList = useMultipleTokensMetadata(tokens, {
    includePrice: true,
  }) as TokenMetadataWithPrice[]

  // builds a map of token metadata for the metadata targets
  const tokenMetadataLookup = useMemo(() => {
    const lookup: TokenMetadataLookup = new Map()

    tokens.forEach((token, index) => {
      const metadata = tokenMetadataList[index]
      if (metadata) {
        lookup.set(
          getTokenMetadataLookupKey(token.chainId, token.address),
          metadata,
        )
      }
    })

    return lookup
  }, [tokens, tokenMetadataList])

  // Build NFT collection metadata targets from NFT state changes
  const nftCollectionTargets = useMemo(() => {
    const targets =
      derivedResult?.nftStateChanges.map((change) => ({
        chainId: change.chainId,
        contractAddress: change.token,
      })) ?? []

    return uniqBy(
      targets,
      (target) => `${target.chainId}-${target.contractAddress.toLowerCase()}`,
    )
  }, [derivedResult])

  // Fetch NFT collection metadata
  const { lookup: nftCollectionMetadataLookup } =
    useNFTCollectionMetadata(nftCollectionTargets)

  // builds the state change items from the derived state change result
  const stateChangeItems = useMemo(() => {
    const combinedResult = derivedResult ?? EMPTY_COMBINED_RESULT

    return buildStateChangeItems({
      combinedResult,
      bridgeStateChanges,
      metadataLookup: tokenMetadataLookup,
      nftCollectionMetadataLookup,
      stateChangesToHide,
    })
  }, [
    derivedResult,
    bridgeStateChanges,
    tokenMetadataLookup,
    nftCollectionMetadataLookup,
    stateChangesToHide,
  ])

  // builds the decoded calls object from the calldata
  const decodedCalls = useMemo<DecodedCallSummary[] | undefined>(() => {
    if (!calls?.length) return undefined

    return calls.map((call, index) => {
      const decodedCall = call as BackendTransactionDecodedCall

      return {
        id: `${call.to}-${index}`,
        target: call.to,
        value: call.value,
        functionName: decodedCall.function,
        args: decodedCall.args,
        data: call.data,
      }
    })
  }, [calls])

  const simulationError =
    error instanceof Error ? (error as SimulationError) : undefined
  const errorMessage = simulationError?.message
  const isSimulationRevert = simulationError?.isSimulationRevert === true
  const failingCallIndexes = simulationError?.failingCallIndexes

  return {
    stateChangeItems,
    isLoading,
    isError,
    isFetching,
    decodedCalls,
    errorMessage,
    isSimulationRevert,
    failingCallIndexes,
  }
}
